import { Bell, Moon, Sun, Contrast } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme, THEMES, type Theme } from '@/contexts/ThemeContext'
import { useAuth } from '@/contexts/AuthContext'
import { useNotifications } from '@/features/notifications/hooks/use-notifications'
import type { NotificationRecipientRole } from '@/types'

/**
 * Top-right FAB cluster for mobile (`lg:hidden`). Pairs theme + notifications
 * with the hamburger at top-left so both ends of the header have primary
 * controls without opening the menu.
 */

const THEME_ICON: Record<Theme, typeof Moon> = {
  dark: Moon,
  dim: Contrast,
  light: Sun,
}

const THEME_LABEL: Record<Theme, string> = {
  dark: 'Escuro',
  dim: 'Penumbra',
  light: 'Claro',
}

function getRecipientRole(personaRole: string): NotificationRecipientRole {
  switch (personaRole) {
    case 'coordinator': return 'coordinator'
    case 'admin': return 'admin'
    case 'client': return 'client'
    default: return 'coordinator'
  }
}

export default function MobileTopActions() {
  const navigate = useNavigate()
  const { theme, cycleTheme } = useTheme()
  const { persona, user } = useAuth()
  const recipientRole = getRecipientRole(persona.role)
  const { notifications, markAsRead, markAllAsRead, unreadCount } = useNotifications(recipientRole, user?.id)

  const [notifOpen, setNotifOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const ThemeIcon = THEME_ICON[theme]
  const nextTheme = THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length]
  const themeTitle = `Tema: ${THEME_LABEL[theme]} → clique para ${THEME_LABEL[nextTheme]}`

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setNotifOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handleThemeClick() {
    document.documentElement.setAttribute('data-theme-switching', '')
    cycleTheme()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.removeAttribute('data-theme-switching')
      })
    })
  }

  function handleNotifClick(n: typeof notifications[number]) {
    setNotifOpen(false)
    if (!n.is_read) markAsRead(n.id)
    if (n.delivery_id) {
      const commentId = (n as { metadata?: { comment_id?: string } }).metadata?.comment_id
      const hash = commentId ? `#comment-${commentId}` : ''
      navigate(`/entrega/${n.delivery_id}${hash}`)
    }
  }

  return (
    <div className="lg:hidden fixed top-3 right-3 z-40 flex items-center gap-2">
      {/* Theme FAB */}
      <button
        onClick={handleThemeClick}
        className="w-10 h-10 rounded-full flex items-center justify-center text-text backdrop-blur-md shadow-sm transition-colors"
        style={{
          background: 'color-mix(in oklch, var(--color-bg) 70%, transparent)',
          boxShadow: '0 0 0 1px var(--color-card-border)',
        }}
        title={themeTitle}
        aria-label={themeTitle}
      >
        <ThemeIcon className="w-4 h-4" />
      </button>

      {/* Notifications FAB */}
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setNotifOpen(!notifOpen)}
          className="relative w-10 h-10 rounded-full flex items-center justify-center text-text backdrop-blur-md shadow-sm transition-colors"
          style={{
            background: 'color-mix(in oklch, var(--color-bg) 70%, transparent)',
            boxShadow: '0 0 0 1px var(--color-card-border)',
          }}
          aria-label="Notificações"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 bg-accent-red text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 leading-none"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* Notifications panel */}
        {notifOpen && (
          <div
            ref={panelRef}
            className="fixed left-3 right-3 top-16 z-[90] rounded-2xl overflow-hidden flex flex-col"
            style={{
              background: 'var(--color-card)',
              border: '1px solid var(--color-card-border)',
              maxHeight: 'calc(100vh - 100px)',
              boxShadow: '0 20px 50px -20px color-mix(in oklch, var(--color-neutral-950) 50%, transparent)',
            }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-card-border">
              <span className="text-[14px] font-extrabold text-bright tk-tight">Notificações</span>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-[11px] font-semibold text-primary hover:underline cursor-pointer"
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-10 text-center">
                  <Bell className="w-6 h-6 text-muted mx-auto mb-2" />
                  <div className="text-[12px] text-dim">Nenhuma notificação</div>
                </div>
              ) : (
                notifications.map(n => (
                  <button
                    key={n.id}
                    onClick={() => handleNotifClick(n)}
                    className="w-full px-4 py-3 text-left border-b border-card-border last:border-b-0 hover:bg-card-hover transition-colors cursor-pointer"
                    style={n.is_read ? undefined : { background: 'var(--color-primary-wash, transparent)' }}
                  >
                    <div className="flex items-start gap-2">
                      {!n.is_read && (
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-bright truncate">{n.title || 'Notificação'}</div>
                        {n.message && (
                          <div className="text-[11px] text-dim mt-0.5 line-clamp-2">{n.message}</div>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
